import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendlyLink } from './MarketingLayout';
import {
  CheckIcon, TrendIcon, MailIcon, PhoneIcon, ChatIcon, RupeeIcon, ClockIcon, ChartIcon,
  BoltIcon, SunIcon, HomeIcon, BriefcaseIcon, GrowthIcon, BagIcon, QuoteIcon,
} from './Icons';

const GiftItem = ({ children }) => (
  <div className="item"><CheckIcon />{children}</div>
);

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const CountUp = ({ to, duration = 1600 }) => {
  const [value, setValue] = useState(() => (prefersReducedMotion() ? to : 0));
  useEffect(() => {
    if (prefersReducedMotion()) return undefined;
    let frame;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      setValue(Math.round(to * (1 - Math.pow(1 - t, 3))));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [to, duration]);
  return value;
};

const SPARK = 'M4 34 L18 31 L32 33 L46 25 L60 27 L74 18 L88 20 L102 11 L116 6';

const Sparkline = () => (
  <svg className="sparkline" viewBox="0 0 120 40" preserveAspectRatio="none" aria-hidden="true">
    <defs>
      <linearGradient id="spark-fill" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#A855F7" stopOpacity="0.35" />
        <stop offset="1" stopColor="#A855F7" stopOpacity="0" />
      </linearGradient>
    </defs>
    <path className="spark-area" d={`${SPARK} L116 40 L4 40 Z`} fill="url(#spark-fill)" />
    <path className="spark-line" d={SPARK} pathLength="1" fill="none" stroke="#A855F7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
  </svg>
);

const SendingAnim = () => (
  <svg className="fc-anim send-anim" viewBox="0 0 30 12" aria-hidden="true">
    {[0, 1, 2].map((i) => (
      <path key={i} d={`M${3 + i * 9} 2 L${8 + i * 9} 6 L${3 + i * 9} 10`} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ animationDelay: `${i * 0.2}s` }} />
    ))}
  </svg>
);

const VoiceAnim = () => (
  <svg className="fc-anim voice-anim" viewBox="0 0 30 16" aria-hidden="true">
    {[0, 1, 2, 3, 4].map((i) => (
      <rect key={i} x={2 + i * 6} y="2" width="2.4" height="12" rx="1.2" fill="currentColor" style={{ animationDelay: `${[0, 0.3, 0.15, 0.45, 0.1][i]}s` }} />
    ))}
  </svg>
);

const TypingAnim = () => (
  <svg className="fc-anim typing-anim" viewBox="0 0 30 12" aria-hidden="true">
    {[0, 1, 2].map((i) => (
      <circle key={i} cx={7 + i * 8} cy="6" r="2.4" fill="currentColor" style={{ animationDelay: `${i * 0.18}s` }} />
    ))}
  </svg>
);

export const Hero = () => (
  <section className="hero">
    <div className="wrap">
      <div className="hero-grid">
        <div>
          <div className="badge"><span className="dot"></span>A ONE-TIME OPPORTUNITY FOR INDIAN BUSINESSES</div>
          <h1>Your business is losing sales<span className="line-2">right now, in real time.</span></h1>
          <p className="lede">Every hour without Salescot is another lead answered too late, another follow-up forgotten, another deal that quietly walked to a competitor.</p>
          <p className="warn"><strong>This isn't a "nice to have"</strong> — it's the fix your business can no longer afford to ignore. We're opening a limited number of free 1:1 consultations this month. When they're gone, this offer will not be repeated.</p>
          <div className="cta-col">
            <CalendlyLink className="btn-primary">Claim Your Free 1:1 Consultation<span className="sub">Limited slots — closes soon</span></CalendlyLink>
            <Link to="/problem" className="btn-secondary">See What It's Costing You</Link>
          </div>
          <div className="gift-list">
            <GiftItem>You leave with a free audit of where your sales process is leaking</GiftItem>
            <GiftItem>No sales pitch, no obligation — yours to keep either way</GiftItem>
          </div>
          <div className="slot-note">Only a handful of consultation slots remain <strong>this month.</strong> Once they're booked, they're booked.</div>
          <div className="trust-row">
            <span>ZERO MONTHLY SALARIES</span>
            <span>LIVE IN 7 DAYS</span>
            <span>NO TECH EXPERIENCE NEEDED</span>
          </div>
        </div>

        <div className="hero-visual">
          <div className="fc-stat">
            <div className="icon-row"><TrendIcon />OUTREACH VOLUME</div>
            <div className="n"><CountUp to={340} />%</div>
            <Sparkline />
            <div className="l">higher outreach volume with the Email Agent</div>
          </div>

          <div className="device">
            <div className="device-bar">
              <span className="dotc"></span><span className="dotc"></span><span className="dotc"></span>
              <span className="device-url">salescot.com/dashboard</span>
            </div>
            <div className="device-body">
              <div className="fc-row">
                <div className="fc-icon"><MailIcon size={16} /></div>
                <div><div className="t">Email Outreach Agent</div><div className="d">Auto follow-ups sent</div></div>
                <SendingAnim />
                <div className="fc-status">Active</div>
              </div>
              <div className="fc-divider"></div>
              <div className="fc-row">
                <div className="fc-icon"><PhoneIcon size={16} width="1.3" /></div>
                <div><div className="t">Bilingual Voice Calling AI</div><div className="d">Call completed · 3m 12s</div></div>
                <VoiceAnim />
                <div className="fc-status">Active</div>
              </div>
              <div className="fc-divider"></div>
              <div className="fc-row">
                <div className="fc-icon"><ChatIcon size={16} /></div>
                <div><div className="t">WhatsApp Sales Agent</div><div className="d">Replied in 2 seconds</div></div>
                <TypingAnim />
                <div className="fc-status">Active</div>
              </div>
            </div>
          </div>

          <div className="fc-chip">
            <div className="pulse-dot"></div>
            <div className="txt">Live now<small>3 leads engaged today</small></div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export const PageHeader = ({ label, title, children }) => (
  <section className="page-header">
    <div className="wrap">
      <div className="section-label">{label}</div>
      <h1>{title}</h1>
      {children && <p className="intro">{children}</p>}
    </div>
  </section>
);

export const BackedBy = () => (
  <section className="backed">
    <div className="wrap">
      <div className="backed-inner">
        <div className="backed-mark">TSP</div>
        <div className="backed-text">
          <div className="k">BUILT BY THE SERIOUS PEOPLE</div>
          <p>Salescot is built by <strong>TSP</strong>, the brand and content strategy team behind campaigns for <strong>Aditya Birla Group</strong>, <strong>Suniel Shetty</strong>, and more. The same strategic thinking now powers your sales team. You don't have to figure this out alone.</p>
        </div>
        <div className="backed-clients">
          <span className="client-chip">Aditya Birla Group</span>
          <span className="client-chip">Suniel Shetty</span>
          <span className="client-chip">100+ businesses</span>
        </div>
      </div>
    </div>
  </section>
);

export const Industries = () => (
  <section className="industries">
    <div className="wrap">
      <div className="label">TRUSTED BY 100+ INDIAN BUSINESSES IN</div>
      <div className="industry-grid">
        <div className="industry-item"><HomeIcon />Real Estate</div>
        <div className="industry-item"><BriefcaseIcon />B2B Agencies</div>
        <div className="industry-item"><GrowthIcon />SaaS &amp; Services</div>
        <div className="industry-item"><BagIcon />D2C Brands</div>
      </div>
    </div>
  </section>
);

export const CostOfInaction = () => (
  <section className="reckoning">
    <div className="wrap">
      <div className="section-label">WHAT THIS IS COSTING YOU</div>
      <h2>If your sales team can't do this today, you already have a leak.</h2>
      <p className="intro">This isn't a hypothetical. It's happening in your inbox and your WhatsApp right now, while your competitors' AI agents already have it covered.</p>
      <div className="cost-grid">
        <div className="cost-cell">
          <RupeeIcon className="cost-icon" />
          <div className="num">₹12,00,000+</div>
          <div className="label">spent every year on a manual sales team that still takes leaves</div>
        </div>
        <div className="cost-cell">
          <ClockIcon className="cost-icon" />
          <div className="num">78%</div>
          <div className="label">of leads go cold after just one hour of silence</div>
        </div>
        <div className="cost-cell">
          <ChartIcon className="cost-icon" />
          <div className="num">41%</div>
          <div className="label">of inquiries arrive outside working hours, and wait</div>
        </div>
        <div className="cost-cell">
          <BoltIcon className="cost-icon" />
          <div className="num">7 days</div>
          <div className="label">is all it takes to have Salescot live and closing for you</div>
        </div>
      </div>
      <div className="reckoning-close">The businesses that fix this now will be the ones still winning a year from now. <span>The rest will wonder what happened.</span></div>
    </div>
  </section>
);

const AgentCard = ({ icon, title, children }) => (
  <div className="agent-card">
    <div className="agent-icon">{icon}</div>
    <h3>{title}</h3>
    <p>{children}</p>
    <span className="active-badge"><span className="p"></span>Active</span>
  </div>
);

export const AgentSuite = () => (
  <section className="suite">
    <div className="wrap">
      <div className="suite-head">
        <div className="section-label">THE SALESCOT AI SUITE</div>
        <h2>Three agents. One suite. The team you should have had years ago.</h2>
      </div>
      <div className="agent-panel">
        <div className="agent-panel-head">
          <div className="name"><span className="name-dot"></span>Salescot AI Suite</div>
          <div className="status-pill"><span className="p"></span>Active 24/7</div>
        </div>
        <div className="agent-grid">
          <AgentCard icon={<MailIcon />} title="Email Outreach Agent">
            340% higher outreach volume &amp; auto follow-ups — no thread ever goes cold again.
          </AgentCard>
          <AgentCard icon={<PhoneIcon />} title="Bilingual Voice Calling AI">
            Outbound calls in Hindi &amp; English, handling objections the way your best closer would.
          </AgentCard>
          <AgentCard icon={<ChatIcon />} title="WhatsApp Sales Agent">
            Instant 2-second replies &amp; catalog booking, right where your leads already are.
          </AgentCard>
        </div>
        <div className="agent-footer-line">100% Automated · <strong>Zero Monthly Salaries</strong></div>
      </div>
    </div>
  </section>
);

export const StatsStrip = () => (
  <div className="stats-strip">
    <div className="wrap">
      <div className="stat-cell">
        <ClockIcon className="stat-icon" />
        <div className="stat-num">2 sec</div><div className="stat-label">average WhatsApp reply time</div>
      </div>
      <div className="stat-cell">
        <SunIcon className="stat-icon" />
        <div className="stat-num">24/7</div><div className="stat-label">coverage, no shift gaps</div>
      </div>
      <div className="stat-cell">
        <TrendIcon className="stat-icon" width="1.7" />
        <div className="stat-num">340%</div><div className="stat-label">higher outreach volume</div>
      </div>
      <div className="stat-cell">
        <RupeeIcon className="stat-icon" />
        <div className="stat-num">₹0</div><div className="stat-label">monthly salaries</div>
      </div>
    </div>
  </div>
);

export const Testimonial = () => (
  <section className="results">
    <div className="wrap testimonial-wrap">
      <div className="section-label centered">RESULTS</div>
      <div className="testimonial">
        <QuoteIcon className="quote-mark" />
        <blockquote>"We stopped losing leads to slow replies. Salescot answers before our old team even saw the notification."</blockquote>
        <div className="who">
          <div className="avatar">MK</div>
          <div className="who-text"><strong>Meera Kulkarni</strong>Head of Growth, real estate brand using Salescot since 2025</div>
        </div>
      </div>
    </div>
  </section>
);

export const FinalCta = () => (
  <section className="final-cta">
    <div className="wrap">
      <div className="ribbon">LAST CALL</div>
      <h2>This consultation won't be offered again once this window closes.</h2>
      <p className="lede">You don't have to figure this out alone. The same TSP team that's built growth strategy for Aditya Birla Group and Suniel Shetty will walk you through exactly where your sales process is leaking — free, in fifteen minutes, with no cost and no pressure. Just a limited number of seats.</p>
      <div className="cta-col">
        <CalendlyLink className="btn-primary">Claim Your Free 1:1 Consultation<span className="sub">Before slots close for this month</span></CalendlyLink>
      </div>
      <div className="gift-list">
        <GiftItem>Free leak-audit, yours to keep even if you don't sign up</GiftItem>
        <GiftItem>15 minutes. No cost. No pressure.</GiftItem>
      </div>
      <div className="slot-note">This is a <strong>once-in-a-while</strong> offer — most businesses only get one chance to take it.</div>
    </div>
  </section>
);
